import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';

import { BaseUI } from '../base.ui';
import { ParentEvents } from '../../utils/parent-events.service';

let nextUniqueId = 0;

@Component({
    selector: 'thr-radio-group',
    styles: [':host { display: block; }'],
    template: '<ng-content></ng-content>',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ThrRadioGroup),
            multi: true,
        },
    ],
})
export class ThrRadioGroup implements AfterContentInit, ControlValueAccessor {
    /** The HTML name attribute applied to radio buttons in this group. */
    private _name = `thr-radio-group-${nextUniqueId++}`;

    /** The currently selected radio button. Should match value. */
    private _selected: ThrRadioButton | null = null;

    /** Whether the `value` has been set to its initial value. */
    private _isInitialized = false;

    /** Whether the radio group is disabled. */
    private _disabled = false;

    /** Whether the radio group is required. */
    private _required = false;

    /** Value assigned to this radio. */
    private _value: any = null;

    /** Child radio buttons. */
    @ContentChildren(forwardRef(() => ThrRadioButton), { descendants: true })
    _radios: QueryList<ThrRadioButton>;

    /** Name of the radio button group. All radio buttons inside this group will use this name. */
    @Input()
    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
        this._updateRadioButtonNames();
    }

    /**
     * Value for the radio-group. Should equal the value of the selected radio button if there is
     * a corresponding radio button with a matching value. If there is not such a corresponding
     * radio button, this value persists to be applied in case a new radio button is added with a
     * matching value.
     */
    @Input()
    get value(): any {
        return this._value;
    }

    set value(newValue: any) {
        if (this._value !== newValue) {
            // Set this before proceeding to ensure no circular loop occurs with selection.
            this._value = newValue;

            this._updateSelectedRadioFromValue();
            this._checkSelectedRadioButton();
        }
    }

    /**
     * The currently selected radio button. If set to a new radio button, the radio group value
     * will be updated to match the new selected button.
     */
    @Input()
    get selected(): ThrRadioButton | null {
        return this._selected;
    }

    set selected(selected: ThrRadioButton | null) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this._checkSelectedRadioButton();
    }

    /** Whether the radio group is disabled */
    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this._markRadiosForCheck();
    }

    /** Whether the radio group is required */
    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this._markRadiosForCheck();
    }

    /**
     * Event emitted when the group value changes.
     * Change events are only emitted when the value changes due to user interaction with
     * a radio button (the same behavior as `<input type-"radio">`).
     */
    // tslint:disable-next-line:no-output-native
    @Output()
    readonly change: EventEmitter<ThrRadioButton> = new EventEmitter<ThrRadioButton>();

    onTouched: () => void = () => {};
    onChange: (value: any) => void = () => {};

    constructor(private _cdr: ChangeDetectorRef) {}

    /**
     * Initialize properties once content children are available.
     * This allows us to propagate relevant attributes to associated buttons.
     */
    ngAfterContentInit() {
        /**
         * Mark this component as initialized in AfterContentInit because the initial value can
         * possibly be set by NgModel on ThrRadioGroup, and it is possible that the OnInit of the
         * NgModel occurs *after* the OnInit of the ThrRadioGroup.
         */
        this._isInitialized = true;
    }

    private _updateRadioButtonNames(): void {
        if (this._radios) {
            this._radios.forEach((radio) => {
                radio.name = this.name;
            });
        }
    }

    /** Updates the `selected` radio button from the internal _value state. */
    private _updateSelectedRadioFromValue(): void {
        // If the value already matches the selected radio, do nothing.
        const isAlreadySelected = this._selected !== null && this._selected.value === this._value;

        if (this._radios && !isAlreadySelected) {
            this._selected = null;
            this._radios.forEach((radio) => {
                radio.checked = this.value === radio.value;
                if (radio.checked) {
                    this._selected = radio;
                }
            });
        }
    }

    private _checkSelectedRadioButton() {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
        }
    }

    private _markRadiosForCheck() {
        if (this._radios) {
            this._radios.forEach((radio) => radio.markForCheck());
        }
    }

    /** Dispatch change event with current selection and group value. */
    emitChangeEvent(): void {
        if (this._isInitialized) {
            this.change.emit(this.selected);
        }
    }

    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(value: any) {
        this.value = value;
        this._cdr.markForCheck();
    }

    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     */
    registerOnChange(fn: (value: any) => void) {
        this.onChange = fn;
    }

    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     */
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     */
    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
        this._cdr.markForCheck();
    }
}

@Component({
    selector: 'thr-radio-button',
    templateUrl: './radio-button.component.html',
    styleUrls: ['./radio-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThrRadioButton extends BaseUI implements OnInit, AfterViewInit, OnDestroy {
    private _uniqueId = `thr-radio-${nextUniqueId++}`;

    /** Whether this radio is checked. */
    private _checked = false;

    /** Whether this radio is disabled. */
    private _disabled: boolean;

    /** Whether this radio is required. */
    private _required: boolean;

    /** Value assigned to this radio. */
    private _value: any = null;

    /** The unique ID for the radio button. */
    @Input()
    id: string = this._uniqueId;

    /** Analog to HTML 'name' attribute used to group radios for unique selection. */
    @Input()
    name: string;

    /** Whether this radio button is checked. */
    @Input()
    get checked(): boolean {
        return this._checked;
    }

    set checked(value: boolean) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;

            if (newCheckedState && this.radioGroup && this.radioGroup.value !== this.value) {
                this.radioGroup.selected = this;
            } else if (!newCheckedState && this.radioGroup && this.radioGroup.value === this.value) {
                /** When unchecking the selected radio button, update the selected radio */
                this.radioGroup.selected = null;
            }

            if (newCheckedState) {
                /** Notify all radio buttons with the same name to un-check. */
                this._radioDispatcher.notify(this.id, this.name);
            }

            this._cdr.markForCheck();
        }
    }

    /** Whether the radio button is disabled. */
    @Input()
    get disabled(): boolean {
        return this._disabled || (this.radioGroup !== null && this.radioGroup.disabled);
    }

    set disabled(value: boolean) {
        const newDisabledState = coerceBooleanProperty(value);
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this._cdr.markForCheck();
        }
    }

    /** Whether the radio button is required. */
    @Input()
    get required(): boolean {
        return this._required || (this.radioGroup && this.radioGroup.required);
    }

    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
    }

    /** The value of this radio button. */
    @Input()
    get value(): any {
        return this._value;
    }

    set value(value: any) {
        if (this._value !== value) {
            this._value = value;
            if (this.radioGroup !== null) {
                if (!this.checked) {
                    // Update checked when the value changed to match the radio group's value
                    this.checked = this.radioGroup.value === value;
                }
                if (this.checked) {
                    this.radioGroup.selected = this;
                }
            }
        }
    }

    @Input()
    size = 100;

    /**
     * Event emitted when the checked state of this radio button changes.
     * Change events are only emitted when the value changes due to user interaction with
     * the radio button (the same behavior as `<input type-"radio">`).
     */
    // tslint:disable-next-line:no-output-native
    @Output()
    readonly change: EventEmitter<ThrRadioButton> = new EventEmitter<ThrRadioButton>();

    /** The native `<input type=radio>` element */
    @ViewChild('input')
    private _inputElement: ElementRef<HTMLInputElement>;

    constructor(
        parentEvents: ParentEvents,
        @Optional() private radioGroup: ThrRadioGroup,
        private _elementRef: ElementRef,
        private _cdr: ChangeDetectorRef,
        private _focusMonitor: FocusMonitor,
        private _radioDispatcher: UniqueSelectionDispatcher
    ) {
        super(parentEvents);
        this._removeUniqueSelectionListener = _radioDispatcher.listen((id: string, name: string) => {
            if (id !== this.id && name === this.name) {
                this.checked = false;
            }
        });
    }

    /** Unregister function for _radioDispatcher */
    private _removeUniqueSelectionListener: () => void = () => {};

    /** ID of the native input element inside `<mat-radio-button>` */
    get inputId(): string {
        return `${this.id || this._uniqueId}-input`;
    }

    ngOnInit() {
        if (this.radioGroup) {
            /** If the radio is inside a radio group, determine if it should be checked */
            this.checked = this.radioGroup.value === this._value;
            /** Copy name from parent radio group */
            this.name = this.radioGroup.name;
        }
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this._focusMonitor.monitor(this._elementRef, true).subscribe((focusOrigin) => {
            if (!focusOrigin && this.radioGroup) {
                this.radioGroup.onTouched();
            }
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this._focusMonitor.stopMonitoring(this._elementRef);
        this._removeUniqueSelectionListener();
    }

    /**
     * Marks the radio button as needing checking for change detection.
     * This method is exposed because the parent radio group will directly
     * update bound properties of the radio button.
     */
    markForCheck() {
        /** When group value changes, the button will not be notified. Use `markForCheck` to explicit update radio button's status */
        this._cdr.markForCheck();
    }

    /**
     * Triggered when the radio button received a click or the input recognized any change.
     * Clicking on a label element, will trigger a change event on the associated input.
     */
    onInputChange(event: Event) {
        /**
         * We always have to stop propagation on the change event.
         * Otherwise the change event, from the input element, will bubble up and
         * emit its event object to the `change` output.
         */
        event.stopPropagation();

        const groupValueChanged = this.radioGroup && this.value !== this.radioGroup.value;
        this.checked = true;
        this._emitChangeEvent();

        if (this.radioGroup) {
            this.radioGroup.onChange(this.value);
            this.radioGroup.onTouched();

            if (groupValueChanged) {
                this.radioGroup.emitChangeEvent();
            }
        }
    }

    /** Dispatch change event with current value. */
    private _emitChangeEvent(): void {
        this.change.emit(this);
    }

    onInputClick(event: Event) {
        /**
         * We have to stop propagation for click events on the visual hidden input element.
         * By default, when a user clicks on a label element, a generated click event will be
         * dispatched on the associated input element. Since we are using a label element as our
         * root container, the click event on the `radio-button` will be executed twice.
         * The real click event will bubble up, and the generated click event also tries to bubble up.
         * This will lead to multiple click events.
         * Preventing bubbling for the second event will solve that issue.
         */
        event.stopPropagation();
    }
}
